import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ActivateSeasonRequest,
  ActivateSeasonResponse,
  SeasonApiRecord,
  SeasonsListResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

export type ActivateSeasonVariables = {
  seasonId: string;
  force?: boolean;
};

const buildActivateSeasonUrl = (seasonId: string): string =>
  API_ROUTES.ACTIVATE_SEASON.replace("{season_id}", seasonId);

const postActivateSeason = async ({
  seasonId,
  force = false,
}: ActivateSeasonVariables): Promise<SeasonApiRecord> => {
  const body: ActivateSeasonRequest = force ? { force: true } : {};
  const response = await getPrivateInstance().post<ActivateSeasonResponse>(
    buildActivateSeasonUrl(seasonId),
    body,
  );
  return unwrapGamePassEntity(response.data, "season");
};

export const useActivateSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  ActivateSeasonVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "activate"],
    mutationFn: postActivateSeason,
    onSuccess: (season) => {
      queryClient.setQueriesData<SeasonsListResponse>(
        { queryKey: ["game-pass", "seasons", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            seasons: current.seasons.map((record) =>
              (record.id ?? record.season_id) === (season.id ?? season.season_id)
                ? { ...record, ...season }
                : record,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
